import { API_BASE_URL, fetchApi } from '@/services/api-client';

import type { EnrolmentStatus } from '../students/students';

export const ATTENDANCE_PATH = '/admin/attendance';

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused' | 'suspended';
export type RoleGroup = 'student' | 'staff';

/**
 * A student's attendance rate for one subject. One declaration for the whole admin app —
 * the lesson-excusal review queue, and per-subject attendance views, all import this so the
 * number a reviewer sees can never drift from what the API returns.
 */
export interface SubjectRate {
  subjectId: string;
  present: number;
  late: number;
  absent: number;
  excused: number;
  /** Lessons inside a suspension — a missed lesson, but never truancy. */
  suspended: number;
  lessonsMarked: number;
  /** Absent + excused + suspended marks, by where the mark came from. */
  missed: {
    teacher: number;
    reportedAbsence: number;
    lateArrival: number;
    lessonExcusal: number;
    suspension: number;
    earlyDismissal: number;
  };
  /** 0–1. Null when `applicable` is false. */
  rate: number | null;
  /** Null when `applicable` is false. */
  belowThreshold: boolean | null;
  applicable: boolean;
}

export type AttendanceTemperatureReading = {
  id: string;
  tempC: number;
  measuredAt: string;
};

export type RosterEntry = {
  userId: string;
  personId: string | null;
  fullName: string;
  displayName: string;
  role: string;
  gradeLevel: number | null;
  photoUrl: string;
  status: AttendanceStatus | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  checkInSource: 'door' | 'manual' | null;
  checkOutSource: 'door' | 'manual' | null;
  lastSeenTime: string | null;
  note: string | null;
  markedBy: { id: string; name: string } | null;
  excusedAbsence: { id: string; type: string; startDate: string; endDate: string } | null;
  temperatureTracking: boolean;
  classId: string | null;
  temperatureReadings: AttendanceTemperatureReading[];
  /** Null on staff rows. */
  enrolmentStatus: EnrolmentStatus | null;
  trialEndDate: string | null;
};

export type DailyGridResponse = {
  date: string;
  isSchoolDay: boolean;
  sessionName: string | null;
  unmarkedCount: number;
  roster: RosterEntry[];
};

export type SchoolCalendarDay = {
  id: string;
  date: string;
  kind: 'open' | 'closed';
  scope: 'full' | 'morning' | 'afternoon';
  audience?: 'all' | 'staff' | 'students';
  label: string | null;
  bellScheduleVariantId: string | null;
};

/** One lesson a student was not present for, named the way a teacher would name it. */
export type LessonGapLesson = {
  periodName: string | null;
  subjectName: string | null;
  status: AttendanceStatus;
};

export type LessonGapDay = {
  date: string;
  /** Present or late on the daily register — what makes a missed lesson worth a second look. */
  atSchool: boolean;
  lessons: LessonGapLesson[];
};

export type LessonGaps = {
  days: LessonGapDay[];
  missedWhileAtSchool: number;
  bySubject: { subjectName: string; missed: number }[];
};

export type PersonHistoryTotals = {
  present: number;
  absent: number;
  late: number;
  excused: number;
  suspended: number;
  schoolDays: number;
  rate: number;
};

export type PersonHistoryRecord = {
  id: string;
  date: string;
  status: AttendanceStatus | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  note: string | null;
};

export type PersonHistory = {
  lessonGaps: LessonGaps | null;
  /** Whose history this is — the page is reached from two lists and needs to say. */
  person: { id: string; displayName: string; role: string };
  records: PersonHistoryRecord[];
  /** Tallied over the requested `from`..`to` window only — the list the card is showing. */
  totals: PersonHistoryTotals;
  /**
   * Tallied over the whole active school year, whatever window was asked for. This is what an
   * attendance rate means; `totals` moves as the window widens, which reads as a wrong number
   * rather than a different question. Null when no school year is active.
   */
  overall: PersonHistoryTotals | null;
  /**
   * How late this person has been over the year, measured from the door against the school's own
   * cutoff — "late eleven times" reads very differently at four minutes than at fifty.
   */
  lateness: {
    count: number;
    totalMinutes: number;
    averageMinutes: number | null;
    worst: { date: string; minutes: number } | null;
    /** Late arrivals excused by an admin — already left out of `count`/`totalMinutes`. */
    excused: number;
  } | null;
  /** A student's attendance per subject over the year; null for staff, empty before any marks. */
  subjectRates:
    | {
        subjectId: string;
        name: string;
        lessonsMarked: number;
        rate: number | null;
        belowThreshold: boolean | null;
        missed: {
          teacher: number;
          reportedAbsence: number;
          lateArrival: number;
          lessonExcusal: number;
          suspension: number;
          earlyDismissal: number;
        };
      }[]
    | null;
};

/** A day's letter in the matrix — an ordinary status, or `LE` for a late excused on file. */
export type MatrixCell = AttendanceStatus | 'LE';

export type MatrixTotals = {
  present: number;
  absent: number;
  late: number;
  excused: number;
  suspended: number;
  unmarked: number;
  /** Late arrivals excused by an admin — already included in `late`, counted again here. */
  lateExcused: number;
};

export type AttendanceReportRow = {
  userId: string;
  personId: string | null;
  fullName: string;
  nickname: string | null;
  gradeLevel: number | null;
  role: string;
  cells: Record<string, MatrixCell>;
  /** `unmarked` = school days in range with no status recorded. */
  totals: MatrixTotals;
};

export type AttendanceMatrix = {
  schoolDays: string[];
  rows: AttendanceReportRow[];
  dayTotals: Record<string, MatrixTotals>;
};

// ─── Period-attendance types ──────────────────────────────────────────────────

export type LessonAssistant = {
  id: string;
  name: string;
  nickname: string | null;
  photoUrl: string;
};

export type MyPeriodEntry = {
  timetableEntryId: string;
  periodId: string;
  periodName: string;
  startTime: string;
  endTime: string;
  classId: string;
  className: string;
  /** Numeric grade, for matching against a student's own `gradeLevel`. */
  gradeLevel: number;
  subjectName: string;
  roomName: string;
  /** The class's assistants plus this lesson's, never the viewer. */
  assistants: LessonAssistant[];
  /** False when the class takes attendance once a day — the lesson is listed for reference only. */
  attendanceRequired: boolean;
  total: number;
  markedCount: number;
};

export type PeriodStudentEntry = {
  studentId: string;
  displayName: string;
  fullName: string;
  gradeLevel: number | null;
  photoUrl: string | null;
  status: AttendanceStatus | null;
  /** Where this mark came from — null/`teacher` means someone chose it directly on this roster. */
  markSource: 'teacher' | 'absence' | 'lateArrival' | 'earlyDismissal' | 'lessonExcusal' | 'suspension' | null;
  /** The school-day register for this date. Null = the day has not been taken yet. */
  dayStatus: AttendanceStatus | null;
  subjectRate: SubjectRate | null;
};

export type PeriodRosterResponse = {
  class: { id: string; name: string; gradeLevel: number | null };
  period: { id: string; name: string; startTime: string; endTime: string } | null;
  subject: { id: string; name: string } | null;
  students: PeriodStudentEntry[];
};

// ─── Subject-rates report types ───────────────────────────────────────────────

export interface SubjectRatesRow {
  studentId: string;
  displayName: string;
  fullName: string;
  gradeLevel: number | null;
  rates: Record<string, SubjectRate>;
  /** Lessons missed in that subject on days the child WAS at school. Absent subjects mean none. */
  missedWhileAtSchool: Record<string, number>;
}

export interface SubjectRatesReportResponse {
  subjects: { id: string; name: string }[];
  rows: SubjectRatesRow[];
}

export type AttendanceAuditStudent = {
  id: string;
  displayName: string;
  fullName: string;
  gradeLevel: number | null;
  photoUrl: string;
};

export type AttendanceAuditLesson = {
  date: string;
  classId: string;
  className: string;
  gradeLevel: number;
  periodId: string;
  periodName: string;
  startTime: string;
  subjectName: string | null;
  teacherName: string | null;
  state: 'missing' | 'partial';
  expected: number;
  marked: number;
  /** Only filled for a partial register. */
  unmarkedStudents: AttendanceAuditStudent[];
};

export interface AttendanceAuditResponse {
  /** The range actually audited: the API clips it to the active school year and to today. */
  from: string;
  to: string;
  unmarkedDays: { date: string; student: AttendanceAuditStudent }[];
  lessons: AttendanceAuditLesson[];
  summary: { unmarkedStudentDays: number; missingRegisters: number; partialRegisters: number };
  /** The office and the SST may fill untaken registers from the daily one. */
  canFill: boolean;
}

export type LessonRegisterRef = { date: string; classId: string; periodId: string };

export interface LessonFillResult {
  registers: number;
  present: number;
  late: number;
  absent: number;
  excused: number;
  skipped: { date: string; className: string; periodName: string; student: AttendanceAuditStudent }[];
}

export type MarkPeriodBody = {
  studentId: string;
  classId: string;
  periodId: string;
  date: string;
  status: 'present' | 'absent' | 'late';
  note?: string;
};

export type MarkPeriodBulkBody = {
  classId: string;
  periodId: string;
  date: string;
  marks: { studentId: string; status: 'present' | 'absent' | 'late' }[];
};

// ─── Attendance summary types ─────────────────────────────────────────────────

export type RoleAttendanceSummary = {
  isSchoolDay: boolean;
  present: number;
  late: number;
  absent: number;
  excused: number;
  suspended: number;
  unmarked: number;
  checkedIn: number;
  onCampus: number;
  total: number;
};

export type AttendanceSummary = {
  date: string;
  isSchoolDay: boolean;
  student: RoleAttendanceSummary;
  staff: RoleAttendanceSummary | null;
};

// ─────────────────────────────────────────────────────────────────────────────

/** One period of a class's day. `subject: null` means the class has no lesson then. */
export type PeriodDayPeriod = {
  periodId: string;
  name: string;
  startTime: string;
  endTime: string;
  subject: { id: string; name: string } | null;
  /** Whether this viewer may mark it — admins always, otherwise the teacher or an assistant. */
  canMark: boolean;
};

export type PeriodDayStudent = {
  studentId: string;
  displayName: string;
  fullName: string;
  photoUrl: string;
  /** periodId → status. Missing means unmarked, which is not the same as having no lesson. */
  statuses: Record<string, AttendanceStatus>;
};

export type PeriodDayResponse = {
  class: { id: string; name: string; gradeLevel: number };
  date: string;
  periods: PeriodDayPeriod[];
  students: PeriodDayStudent[];
};

export const attendanceApi = {
  daily: (params: { date: string; role: RoleGroup; gradeLevel?: number; search?: string }) => {
    const qs = new URLSearchParams({ date: params.date, role: params.role });
    if (params.gradeLevel !== undefined) qs.append('gradeLevel', String(params.gradeLevel));
    if (params.search) qs.append('search', params.search);
    return fetchApi<DailyGridResponse>(`${ATTENDANCE_PATH}?${qs.toString()}`);
  },

  getSummary: (date: string) =>
    fetchApi<AttendanceSummary>(`${ATTENDANCE_PATH}/summary?date=${encodeURIComponent(date)}`),

  mark: (data: {
    userId: string;
    date: string;
    status: 'present' | 'absent' | 'late';
    // `null` explicitly clears a stored time; `undefined` would be dropped by
    // JSON.stringify, causing the server to overwrite the saved value with null.
    checkInTime?: string | null;
    checkOutTime?: string | null;
    note?: string | null;
  }) => fetchApi(`${ATTENDANCE_PATH}`, { method: 'PUT', body: JSON.stringify(data) }),

  personHistory: (userId: string, from: string, to: string) =>
    fetchApi<PersonHistory>(`${ATTENDANCE_PATH}/person/${userId}?from=${from}&to=${to}`),

  report: (params: { from: string; to: string; role: RoleGroup; gradeLevel?: number }) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to, role: params.role });
    if (params.gradeLevel !== undefined) qs.append('gradeLevel', String(params.gradeLevel));
    return fetchApi<AttendanceMatrix>(`${ATTENDANCE_PATH}/report?${qs.toString()}`);
  },

  exportUrl: (params: { from: string; to: string; role: RoleGroup; gradeLevel?: number }) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to, role: params.role });
    if (params.gradeLevel !== undefined) qs.append('gradeLevel', String(params.gradeLevel));
    return `${API_BASE_URL}${ATTENDANCE_PATH}/report/export?${qs.toString()}`;
  },

  listCalendar: (from: string, to: string) =>
    fetchApi<SchoolCalendarDay[]>(`${ATTENDANCE_PATH}/calendar?from=${from}&to=${to}`),

  upsertCalendar: (data: {
    date: string;
    kind: 'open' | 'closed';
    scope?: string;
    audience?: 'all' | 'staff' | 'students';
    label?: string;
    bellScheduleVariantId?: string | null;
  }) => fetchApi<SchoolCalendarDay>(`${ATTENDANCE_PATH}/calendar`, { method: 'POST', body: JSON.stringify(data) }),

  removeCalendar: (date: string) => fetchApi(`${ATTENDANCE_PATH}/calendar/${date}`, { method: 'DELETE' }),

  /** Whether the caller teaches anything at all — used to decide if "Classes today" is offered. */
  teachesAny: () => fetchApi<{ teaches: boolean; hasLessons: boolean }>(`${ATTENDANCE_PATH}/teaches-any`),

  myPeriods: (date: string) =>
    fetchApi<MyPeriodEntry[]>(`${ATTENDANCE_PATH}/my-periods?date=${encodeURIComponent(date)}`),

  /** Grades the caller homerooms in the active year. No date — homeroom is a standing role. */
  myHomeroomGrades: () => fetchApi<number[]>(`${ATTENDANCE_PATH}/my-homeroom-grades`),

  periodDay: (classId: string, date: string) => {
    const qs = new URLSearchParams({ classId, date });
    return fetchApi<PeriodDayResponse>(`${ATTENDANCE_PATH}/period-day?${qs.toString()}`);
  },
  periodRoster: (classId: string, periodId: string, date: string) => {
    const qs = new URLSearchParams({ classId, periodId, date });
    return fetchApi<PeriodRosterResponse>(`${ATTENDANCE_PATH}/period-roster?${qs.toString()}`);
  },

  subjectRates: (params: { gradeLevel?: number; classId?: string; subjectId?: string }) => {
    const qs = new URLSearchParams();
    if (params.gradeLevel !== undefined) qs.set('gradeLevel', String(params.gradeLevel));
    if (params.classId) qs.set('classId', params.classId);
    if (params.subjectId) qs.set('subjectId', params.subjectId);
    return fetchApi<SubjectRatesReportResponse>(`${ATTENDANCE_PATH}/subject-rates?${qs.toString()}`);
  },

  audit: (params: { from: string; to: string; gradeLevel?: number }) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to });
    if (params.gradeLevel !== undefined) qs.set('gradeLevel', String(params.gradeLevel));
    return fetchApi<AttendanceAuditResponse>(`${ATTENDANCE_PATH}/audit?${qs.toString()}`);
  },

  fillLessons: (data: { registers: LessonRegisterRef[]; dryRun?: boolean }) =>
    fetchApi<LessonFillResult>(`${ATTENDANCE_PATH}/audit/fill-lessons`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  markPeriod: (data: MarkPeriodBody) =>
    fetchApi(`${ATTENDANCE_PATH}/period-mark`, { method: 'PUT', body: JSON.stringify(data) }),

  markPeriodBulk: (data: MarkPeriodBulkBody) =>
    fetchApi(`${ATTENDANCE_PATH}/period-mark/bulk`, { method: 'PUT', body: JSON.stringify(data) }),

  recordCheckinTemperature: (data: {
    studentId: string;
    classId: string;
    date: string;
    tempC: number;
    note?: string;
  }) => fetchApi(`${ATTENDANCE_PATH}/temperature`, { method: 'POST', body: JSON.stringify(data) }),
};
